/** Expense and income maths for the ledger, plus bringing over entries saved by the earlier tracker. Pure. */
import type { Expense, ExpenseKind } from "./types";
import { expenseGross, isPledged, UNCATEGORISED } from "./budgetOps";

const round2 = (n: number) => Math.round(n * 100) / 100;
const norm = (s: string | undefined) => (s ?? "").trim().toLowerCase().replace(/\s+/g, " ");

export interface LedgerSummary {
  cost: number;
  income: number;
  /** Income promised but not yet received; not part of income or net. */
  pledged: number;
  net: number;
  /** Costs by category, biggest first. */
  byCategory: { category: string; amount: number }[];
  otherCurrency: number;
}

export function ledgerSummary(expenses: readonly Expense[], currency: string): LedgerSummary {
  let cost = 0, income = 0, pledged = 0, otherCurrency = 0;
  const map = new Map<string, { category: string; amount: number }>();
  for (const e of expenses) {
    if (e.currency && e.currency !== currency) { otherCurrency++; continue; }
    const g = expenseGross(e);
    if (isPledged(e)) { pledged += g; continue; }
    if (e.kind === "income") { income += g; continue; }
    cost += g;
    const key = norm(e.category) || norm(UNCATEGORISED);
    const row = map.get(key) ?? { category: (e.category ?? "").trim() || UNCATEGORISED, amount: 0 };
    row.amount = round2(row.amount + g);
    map.set(key, row);
  }
  cost = round2(cost); income = round2(income); pledged = round2(pledged);
  return { cost, income, pledged, net: round2(income - cost), otherCurrency, byCategory: Array.from(map.values()).sort((a, b) => b.amount - a.amount || a.category.localeCompare(b.category)) };
}

/** Entries from the earlier tracker: {date, category, description, amount}. Anything unusable is skipped. */
export function migrateLegacyExpenses(raw: unknown, makeId: () => string, currency: string, today: string): Expense[] {
  if (!Array.isArray(raw)) return [];
  const out: Expense[] = [];
  for (const r of raw.slice(0, 5000)) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    const amount = typeof o.amount === "number" ? o.amount : Number(o.amount);
    if (!Number.isFinite(amount) || amount <= 0) continue;
    const date = typeof o.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(o.date) ? o.date : today;
    const category = typeof o.category === "string" ? o.category.trim().slice(0, 300) : "";
    const description = (typeof o.description === "string" ? o.description.trim() : "") || category || "Expense";
    out.push({ id: makeId(), date, description: description.slice(0, 300), kind: "cost" as ExpenseKind, category: category || undefined, amount: round2(amount), currency, taxType: "none", taxIncluded: false });
  }
  return out;
}
